import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { jobService } from "../services/job.service";
import PromptInput from "../components/common/ui/PromptInput";

const STORAGE_KEY = 'jobDescription';

const GeneratedJobDescription = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatedData, setGeneratedData] = useState(null);
  const [inputContent, setInputContent] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const generationAttempted = useRef(false);
  const [generatingInitial, setGeneratingInitial] = useState(false);

  useEffect(() => {
    let mounted = true;

    const generateDescription = async () => {
      if (generationAttempted.current) return;
      generationAttempted.current = true;

      try {
        if (!location.state) {
          navigate("/jobs/create");
          return;
        }

        setGeneratingInitial(true);
        setLoading(true);

        const { jobTitle, department, qualifications, responsibilities } = location.state;
        
        const jobResponse = await jobService.createJob({
          jobTitle,
          department,
          qualifications,
          responsibilities
        });

        if (!mounted) return;

        if (jobResponse.data) {
          const jobData = jobResponse.data;
          
          // Handle both create API and continue API response structures
          let generatedContent = {
            overview: "No overview available",
            qualifications: ["No qualifications generated"],
            responsibilities: ["No responsibilities generated"],
            requirements: ["No requirements generated"]
          };

          // Check if we have jobDescription from create API
          if (jobData.jobDescription) {
            generatedContent = {
              overview: jobData.jobDescription.overview || "No overview available",
              qualifications: Array.isArray(jobData.jobDescription.generatedQualifications) 
                ? jobData.jobDescription.generatedQualifications 
                : jobData.jobDescription.generatedQualifications 
                  ? [jobData.jobDescription.generatedQualifications]
                  : ["No qualifications generated"],
              responsibilities: Array.isArray(jobData.jobDescription.generatedResponsibilities) 
                ? jobData.jobDescription.generatedResponsibilities 
                : jobData.jobDescription.generatedResponsibilities 
                  ? [jobData.jobDescription.generatedResponsibilities]
                  : ["No responsibilities generated"],
              requirements: Array.isArray(jobData.jobDescription.requirements) 
                ? jobData.jobDescription.requirements 
                : jobData.jobDescription.requirements 
                  ? [jobData.jobDescription.requirements]
                  : ["No requirements generated"]
            };
          }
          // Check if we have content from continue API structure
          else if (jobData.content?.content) {
            const content = jobData.content.content;
            generatedContent = {
              overview: content.overview || "No overview available",
              qualifications: Array.isArray(content.qualifications) 
                ? content.qualifications 
                : content.qualifications 
                  ? [content.qualifications]
                  : ["No qualifications generated"],
              responsibilities: Array.isArray(content.responsibilities) 
                ? content.responsibilities 
                : content.responsibilities 
                  ? [content.responsibilities]
                  : ["No responsibilities generated"],
              requirements: Array.isArray(content.requirements) 
                ? content.requirements 
                : content.requirements 
                  ? [content.requirements]
                  : ["No requirements generated"]
            };
          }

          const formattedData = {
            jobTitle: jobData.title,
            department: jobData.department,
            qualifications: jobData.qualifications,
            responsibilities: jobData.responsibilities,
            _id: jobData._id,
            jobId: jobData.jobId,
            threadId: jobData.threadId,
            assistantId: jobData.assistantId,
            createdBy: jobData.createdBy,
            generatedContent: generatedContent
          };

          // Store in localStorage
          localStorage.setItem(`${STORAGE_KEY}_${jobData._id}`, JSON.stringify(formattedData));
          setGeneratedData(formattedData);
        } else {
          throw new Error('Job description not generated');
        }
      } catch (err) {
        if (mounted) {
          console.error("Error generating description:", err);
          setError(err.response?.data?.message || "Failed to generate job description");
        }
      } finally {
        if (mounted) {
          setGeneratingInitial(false);
          setLoading(false);
        }
      }
    };

    generateDescription();

    return () => {
      mounted = false;
    };
  }, [location.state, navigate]);

  // Update localStorage when content changes
  useEffect(() => {
    if (generatedContent && generatedData?._id) {
      const updatedData = {
        ...generatedData,
        generatedContent: generatedContent
      };
      localStorage.setItem(`${STORAGE_KEY}_${generatedData._id}`, JSON.stringify(updatedData));
    }
  }, [generatedContent, generatedData]);

  const handleGenerateContent = async () => {
    if (!inputContent.trim()) return;
    setIsGenerating(true);
  
    try {
      // Prepare existing content for continuation
      const existingContent = generatedContent ? {
        overview: generatedContent.overview,
        qualifications: generatedContent.qualifications,
        responsibilities: generatedContent.responsibilities,
        requirements: generatedContent.requirements
      } : null;

      const response = await jobService.continueJobDescription(generatedData._id, {
        user_message: inputContent.trim(), // Only send if not empty
        existing_content: existingContent
      });

      console.log('Continue response in component:', response);
      console.log('Response.content:', response.content);
      console.log('Response.content.content:', response.content?.content);

      // Handle nested content structure - API returns response.content.content
      const content = response.content?.content || response.content || response.data?.content;
      
      console.log('Extracted content:', content);
      
      if (content) {
        // Ensure we have arrays for all fields and provide fallbacks
        const processedContent = {
          overview: content.overview || generatedData.generatedContent?.overview || "No overview available",
          qualifications: Array.isArray(content.qualifications) 
            ? content.qualifications 
            : content.qualifications 
              ? [content.qualifications]
              : generatedData.generatedContent?.qualifications || ["No qualifications generated"],
          responsibilities: Array.isArray(content.responsibilities) 
            ? content.responsibilities 
            : content.responsibilities 
              ? [content.responsibilities]
              : generatedData.generatedContent?.responsibilities || ["No responsibilities generated"],
          requirements: Array.isArray(content.requirements) 
            ? content.requirements 
            : content.requirements 
              ? [content.requirements]
              : generatedData.generatedContent?.requirements || ["No requirements generated"]
        };
        console.log('Processed content:', processedContent);
        setGeneratedContent(processedContent);
        setInputContent(''); // Clear input after successful generation
        
        // Update localStorage with new content
        const updatedData = {
          ...generatedData,
          generatedContent: processedContent
        };
        localStorage.setItem(`${STORAGE_KEY}_${generatedData._id}`, JSON.stringify(updatedData));
      } else {
        console.error('No content found in response:', response);
        throw new Error("Received invalid response format from the service");
      }
    } catch (err) {
      console.error("Error generating new content:", err);
      setError(err.message || "Failed to generate new content");
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleSaveJob = async () => {
    try {
      if (!generatedContent) {
        navigate(`/jobs/${generatedData._id}`);
        return;
      }

      const response = await jobService.updateJobDescription(generatedData._id, {
        overview: generatedContent.overview,
        qualifications: generatedContent.qualifications,
        responsibilities: generatedContent.responsibilities,
        requirements: generatedContent.requirements
      });
      
      console.log("Save job response:", response);
      if (response.success) {
        navigate(`/jobs/${generatedData._id}`);
      }
    } catch (err) {
      console.error("Error saving job:", err);
      setError("Failed to save job. Please try again.");
    }
  };

  if (error) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
          <button 
            onClick={() => navigate("/jobs/create")} 
            className="ml-4 text-orange-500 hover:text-orange-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (generatingInitial) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Generating Job Description...</p>
        </div>
      </div>
    );
  }

  if (!generatedData) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="m-6 p-6 bg-white h-[95%] flex flex-col justify-between rounded-xl shadow-sm">
      <div>
        <div className="flex items-center gap-4 mb-6">
          <div className="flex gap-2">
            <div className="px-3 py-1 bg-orange-100 text-orange-600 rounded-full text-sm">
              {generatedData.jobTitle}
            </div>
            {/* <div className="px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-sm">
              Full time
            </div> */}
            <div className="px-3 py-1 bg-purple-100 text-purple-600 rounded-full text-sm">
              {generatedData.department}
            </div>
          </div>
        </div>
        
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-3">Overview:</h3>
            <p className="text-gray-600">
              {generatedContent?.overview ||
                generatedData.generatedContent.overview}
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-3">
              Key Responsibilities:
            </h3>
            <ul className="list-disc pl-5 space-y-2 text-gray-600">
              {(
                generatedContent?.responsibilities || 
                generatedData.generatedContent.responsibilities
              ).map((resp, index) => (
                <li key={index}>{resp}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-3">Qualifications:</h3>
            <ul className="list-disc pl-5 space-y-2 text-gray-600">
              {(
                generatedContent?.qualifications || 
                generatedData.generatedContent.qualifications
              ).map((qual, index) => (
                <li key={index}>{qual}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-3">Requirements:</h3>
            <ul className="list-disc pl-5 space-y-2 text-gray-600">
              {(
                generatedContent?.requirements ||
                generatedData.generatedContent.requirements
              ).map((req, index) => (
                <li key={index}>{req}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <PromptInput
        value={inputContent}
        onChange={setInputContent}
        onSubmit={handleGenerateContent}
        onSave={handleSaveJob}
        loading={isGenerating}
        placeholder="Enter your prompt..."
        showSaveButton={true}
        saveButtonText="Save & Continue"
        disabled={false}
      />
    </div>
  );
};

export default GeneratedJobDescription;